import { useDrop, useDragLayer } from 'react-dnd';
import { useRef, useState, useEffect, useMemo } from 'react';
import { cn } from '@/app/components/ui/utils';
import { TechCard, TECH_CARD_TYPE } from './tech-card';
import type { BoardProject, BoardTech, BoardAssignment, CrewListItem, BoardTerminology } from '@/hooks/use-staffing-board';
import { DEFAULT_TERMINOLOGY } from '@/hooks/use-staffing-board';
import { MapPin, Building2, AlertTriangle, ClipboardList, Package, DollarSign, Users, ChevronDown, Check, ArrowDownToLine, FolderOpen, FileText, Shield, RotateCcw, Phone, User, Truck, CheckCircle2, XOctagon, Send, Briefcase, CloudSun, Eye, EyeOff } from 'lucide-react';
import { useIsTouchDevice } from '@/hooks/use-touch-device';
import { formatMoney, OPTIONAL_FRACTION_DIGITS } from '@/lib/currency';
import {
  getCardReadinessReasons,
  getCardReadinessStatus,
  type CardReadinessOptions,
  type CardReadinessReason,
  type CardReadinessStatus,
} from './card-readiness';

// ── Dispatch Readiness Chip ──────────────────────────────────────────────────

export type { CardReadinessStatus } from './card-readiness';

export function computeCardReadiness(
  project: BoardProject,
  techs: BoardTech[],
  opts: CardReadinessOptions = {},
): CardReadinessStatus {
  const reasons = getCardReadinessReasons(project, techs, opts);
  return getCardReadinessStatus(project, reasons);
}

const READINESS_CHIP_STYLES: Record<CardReadinessStatus, { bg: string; text: string; ring: string; label: string }> = {
  ready: { bg: 'bg-green-50', text: 'text-green-700', ring: 'ring-green-600/20', label: 'Ready' },
  warning: { bg: 'bg-amber-50', text: 'text-amber-700', ring: 'ring-amber-600/20', label: 'Warning' },
  blocked: { bg: 'bg-red-50', text: 'text-red-700', ring: 'ring-red-600/20', label: 'Blocked' },
  dispatched: { bg: 'bg-blue-50', text: 'text-blue-700', ring: 'ring-blue-600/20', label: 'Dispatched' },
};

// A null count means the caller has no H3Safe access: show the flag only.
function safetyEventLabel(
  count: number | null | undefined,
  flagged: boolean | undefined,
  singular: string,
  plural: string,
  flagLabel: string,
): string | null {
  if (count != null) return count > 0 ? `${count} ${count === 1 ? singular : plural}` : null;
  return flagged ? flagLabel : null;
}

function deliveryLabel(deliveryStatus: string | null | undefined): string | null {
  if (!deliveryStatus) return null;
  if (deliveryStatus === 'no_channel') return 'Not Reached';
  if (deliveryStatus === 'failed') return 'Delivery Failed';
  return null;
}

function DispatchReadinessChip({
  status,
  reasons,
  deliveryStatus,
}: {
  status: CardReadinessStatus;
  reasons: CardReadinessReason[];
  deliveryStatus?: string | null;
}) {
  const deliveryIssue = status === 'dispatched' ? deliveryLabel(deliveryStatus) : null;
  const style = deliveryIssue
    ? { bg: 'bg-amber-50', text: 'text-amber-700', ring: 'ring-amber-600/20' }
    : READINESS_CHIP_STYLES[status];
  const label = deliveryIssue ?? READINESS_CHIP_STYLES[status].label;
  return (
    <span className="group relative inline-flex">
      <span
        className={cn(
          'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium ring-1 ring-inset',
          style.bg, style.text, style.ring,
        )}
        data-testid="dispatch-readiness-chip"
        data-status={status}
        data-delivery-status={deliveryStatus ?? undefined}
        aria-label={[
          `Dispatch readiness: ${label}`,
          ...reasons.map((reason) => reason.message),
        ].join('. ')}
      >
        {!deliveryIssue && status === 'ready' && <CheckCircle2 className="h-3 w-3" />}
        {(deliveryIssue || status === 'warning') && <AlertTriangle className="h-3 w-3" />}
        {!deliveryIssue && status === 'blocked' && <XOctagon className="h-3 w-3" />}
        {!deliveryIssue && status === 'dispatched' && <Send className="h-3 w-3" />}
        {label}
      </span>
      <span
        className="pointer-events-none absolute right-0 top-full z-40 mt-1.5 hidden w-max max-w-[17rem] rounded-md bg-gray-900 px-2.5 py-2 text-left text-[11px] font-normal text-white shadow-lg group-hover:block"
        data-testid="readiness-reasons-tooltip"
      >
        {reasons.length > 0 ? (
          <span className="flex flex-col gap-1">
            {reasons.map((reason) => (
              <span key={reason.key} className="flex items-start gap-1.5">
                <span className={cn(
                  'mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full',
                  reason.severity === 'blocked' ? 'bg-red-400' : 'bg-amber-300',
                )} />
                <span>{reason.message}</span>
              </span>
            ))}
          </span>
        ) : (
          <span>{deliveryIssue ?? (status === 'dispatched' ? 'Already dispatched' : 'No readiness issues')}</span>
        )}
      </span>
    </span>
  );
}

function WeatherCurrentChip({ weather }: { weather: NonNullable<BoardProject['weather_current']> }) {
  const tempC = Math.round(weather.temperature_c);
  const tempF = Math.round(weather.temperature_f);

  return (
    <span
      className="inline-flex items-center gap-0.5 rounded-full bg-sky-50 px-1.5 py-0.5 text-[10px] font-medium text-sky-700 ring-1 ring-inset ring-sky-600/20"
      data-testid="weather-current-chip"
      title={`${weather.condition} ${tempC}°C / ${tempF}°F`}
    >
      <CloudSun className="h-3 w-3" />
      <span className="max-w-[7rem] truncate">{weather.condition}</span>
      <span>{tempC}°C</span>
    </span>
  );
}

// ── Back-of-card content ─────────────────────────────────────────────────────

interface ProjectBackCardProps {
  project: BoardProject;
  techs: BoardTech[];
  terminology: BoardTerminology;
  readinessStatus: CardReadinessStatus;
  readinessReasons: CardReadinessReason[];
  projectsEnabled: boolean;
  inventoryEnabled: boolean;
  crewEnabled: boolean;
  showFinancials: boolean;
  costsRevealed: boolean;
  coLocatedCount?: number;
  coLocatedProjects?: { id: number; name: string }[];
  coLocatedFilterActive?: boolean;
  supervisionGap?: boolean;
  onToggleCosts?: () => void;
  onCoLocatedClick?: () => void;
  onSupervisionGapClick?: () => void;
  onWorkPackage?: (projectId: number) => void;
  onFindCrew?: (projectId: number) => void;
  canEdit: boolean;
  currency: string;
}

function ProjectBackCard({
  project,
  techs,
  terminology,
  readinessStatus,
  readinessReasons,
  projectsEnabled,
  inventoryEnabled,
  crewEnabled,
  showFinancials,
  costsRevealed,
  coLocatedCount,
  coLocatedProjects,
  coLocatedFilterActive,
  supervisionGap,
  onToggleCosts,
  onCoLocatedClick,
  onSupervisionGapClick,
  onWorkPackage,
  onFindCrew,
  canEdit,
  currency,
}: ProjectBackCardProps) {
  const addressParts = [
    project.location,
    project.address,
    [project.city, project.state_province].filter(Boolean).join(', '),
  ].filter(Boolean);

  const hasAddress = addressParts.length > 0;
  const hasClientContact = project.client_contact_name || project.client_contact_phone;
  const safetyEvents = [
    safetyEventLabel(project.open_near_misses, project.has_open_near_misses, 'near-miss', 'near-misses', 'Open near-miss'),
    safetyEventLabel(project.open_incidents, project.has_open_incidents, 'incident', 'incidents', 'Open incident'),
  ].filter(Boolean);

  return (
    <div className="px-3 py-2.5 space-y-2 text-xs" onClick={(e) => e.stopPropagation()}>
      <div className="grid grid-cols-2 gap-x-3 gap-y-2">
        <div>
          <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">Job number</div>
          <div className="text-gray-700">{project.job_number || '\u2014'}</div>
        </div>
        <div>
          <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">Project number</div>
          <div className="text-gray-700">{project.project_number || '\u2014'}</div>
        </div>
      </div>

      <div>
        <div className="flex items-center gap-1 text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-0.5">
          <MapPin className="h-3 w-3" />
          {terminology.location_label}
        </div>
        {hasAddress ? (
          <div className="text-gray-700 leading-relaxed">
            {addressParts.map((part, i) => (
              <span key={i} className="block">{part}</span>
            ))}
          </div>
        ) : (
          <span className="text-gray-400">&mdash;</span>
        )}
        {(project.zone_name || project.spot_name) && (
          <div className="mt-1 text-gray-500" data-testid="zone-spot-label">
            {[project.zone_name, project.spot_name].filter(Boolean).join(' / ')}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center gap-1 text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-0.5">
          <Building2 className="h-3 w-3" />
          Client
        </div>
              {project.client_name && (
                <div className="text-gray-700 mb-0.5">{project.client_name}</div>
              )}
        {hasClientContact ? (
          <div className="text-gray-700">
            {project.client_contact_name && (
              <span className="flex items-center gap-1">
                <User className="h-3 w-3 text-gray-400" />
                {project.client_contact_name}
              </span>
            )}
            {project.client_contact_phone && (
              <span className="flex items-center gap-1 mt-0.5">
                <Phone className="h-3 w-3 text-gray-400" />
                {project.client_contact_phone}
              </span>
            )}
          </div>
        ) : (
          <span className="text-gray-400">&mdash;</span>
        )}
      </div>

      <div>
        <div className="flex items-center gap-1 text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-0.5">
          <User className="h-3 w-3" />
          Supervisor
        </div>
        <span className="text-gray-700">
          {project.supervisor_name || '\u2014'}
        </span>
      </div>

      <div className="border-t border-gray-100 pt-2">
        <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-1">
          {readinessReasons.length > 0 ? "What's wrong" : 'Readiness'}
        </div>
        {readinessReasons.length > 0 ? (
          <ul className="space-y-1.5" data-testid="readiness-reasons-list">
            {readinessReasons.map((reason) => (
              <li
                key={reason.key}
                className={cn(
                  'flex items-start gap-1.5 rounded-md px-2 py-1.5 text-[11px] font-medium',
                  reason.severity === 'blocked'
                    ? 'bg-red-50 text-red-700'
                    : 'bg-amber-50 text-amber-700',
                )}
                data-severity={reason.severity}
              >
                {reason.severity === 'blocked' ? (
                  <XOctagon className="mt-0.5 h-3 w-3 flex-shrink-0" />
                ) : (
                  <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0" />
                )}
                <span>{reason.message}</span>
              </li>
            ))}
          </ul>
        ) : (
          <div
            className={cn(
              'inline-flex items-center gap-1 text-[11px] font-medium',
              readinessStatus === 'dispatched' ? 'text-blue-700' : 'text-green-700',
            )}
            data-testid="readiness-clear"
          >
            {readinessStatus === 'dispatched' ? (
              <Send className="h-3 w-3" />
            ) : (
              <CheckCircle2 className="h-3 w-3" />
            )}
            {readinessStatus === 'dispatched' ? 'Already dispatched' : 'No readiness issues'}
          </div>
        )}
      </div>

      {(project.weather_current || project.weather) && (
        <div>
          <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-1">Conditions</div>
          <div className="flex flex-wrap items-center gap-1.5">
          {project.weather_current && <WeatherCurrentChip weather={project.weather_current} />}
          {project.weather && (
            <span
              className="inline-flex items-center gap-0.5 rounded-full bg-sky-50 px-1.5 py-0.5 text-[10px] font-medium text-sky-700 ring-1 ring-inset ring-sky-600/20"
              data-testid="weather-chip"
            >
              <CloudSun className="h-3 w-3" />
              {project.weather}
            </span>
          )}
          </div>
        </div>
      )}

      {projectsEnabled && project.budget_utilization != null && (
        <div data-testid="budget-utilization-chip">
          <div className="flex items-center gap-1 text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-1">
            <DollarSign className="h-3 w-3" />
            Budget
          </div>
          <div className="flex items-center gap-1">
            <div className="h-1.5 flex-1 rounded-full bg-gray-200 overflow-hidden">
              <div
                className={cn(
                  'h-full rounded-full transition-all',
                  project.budget_utilization > 90
                    ? 'bg-red-500'
                    : project.budget_utilization > 75
                      ? 'bg-amber-500'
                      : 'bg-green-500',
                )}
                style={{ width: `${Math.min(project.budget_utilization, 100)}%` }}
              />
            </div>
            <span
              className={cn(
                'text-[10px] font-medium',
                project.budget_utilization > 90
                  ? 'text-red-700'
                  : project.budget_utilization > 75
                    ? 'text-amber-700'
                    : 'text-green-700',
              )}
            >
              {project.budget_utilization}%
            </span>
          </div>
          {(project.budget != null || project.budget_spent != null) && (
            <div className="mt-1 text-gray-500">
              {project.budget_spent != null && `${formatMoney(project.budget_spent, currency, OPTIONAL_FRACTION_DIGITS)} spent`}
              {project.budget != null && ` of ${formatMoney(project.budget, currency, OPTIONAL_FRACTION_DIGITS)}`}
            </div>
          )}
        </div>
      )}

      {coLocatedCount != null && coLocatedCount >= 2 && (
        <div data-testid="co-located-chip">
          <button
            type="button"
            onClick={onCoLocatedClick}
            className={cn(
              'group relative inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset transition-colors',
              coLocatedFilterActive
                ? 'bg-purple-600 text-white ring-purple-700 hover:bg-purple-700'
                : 'bg-purple-50 text-purple-700 ring-purple-600/20 hover:bg-purple-100',
            )}
          >
            <MapPin className="h-3 w-3" />
            {coLocatedCount} co-located
            {coLocatedProjects && coLocatedProjects.length > 0 && (
              <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:flex flex-col w-max max-w-[220px] rounded-md bg-gray-900 px-2.5 py-1.5 text-[10px] text-white shadow-lg z-30">
                {coLocatedProjects.filter((p) => p.id !== project.id).map((p) => (
                  <span key={p.id} className="py-0.5 truncate">{p.name}</span>
                ))}
              </span>
            )}
          </button>
        </div>
      )}

      {!project.staffing_requirement && project.has_unmet_staffing_requirement && (
        <div data-testid="staffing-requirement-flag">
          <span className="inline-flex items-center rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-inset ring-amber-600/20">
            Staffing requirement not met
          </span>
        </div>
      )}
      {project.staffing_requirement && (
        <div data-testid="staffing-requirement-chips">
          <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-1">Staffing requirements</div>
          <div className="flex flex-wrap items-center gap-1">
            {([
              ['L3', project.staffing_requirement.satisfied_l3, project.staffing_requirement.required_l3],
              ['L2', project.staffing_requirement.satisfied_l2, project.staffing_requirement.required_l2],
              ['L1', project.staffing_requirement.satisfied_l1, project.staffing_requirement.required_l1],
            ] as const).filter(([, , required]) => required > 0).map(([label, satisfied, required]) => (
              <span
                key={label}
                className={cn(
                  'inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ring-1 ring-inset',
                  satisfied >= required && 'bg-green-50 text-green-700 ring-green-600/20',
                  satisfied > 0 && satisfied < required && 'bg-amber-50 text-amber-700 ring-amber-600/20',
                  satisfied === 0 && 'bg-red-50 text-red-700 ring-red-600/20',
                )}
              >
                {satisfied}/{required} {label}
              </span>
            ))}
          </div>
        </div>
      )}

      {supervisionGap && (
        <div data-testid="supervision-gap-badge">
          <button
            type="button"
            onClick={onSupervisionGapClick}
            className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-700 ring-1 ring-inset ring-indigo-600/20 hover:bg-indigo-100 transition-colors"
          >
            <Shield className="h-3 w-3" />
            Supervision gap
          </button>
        </div>
      )}

      {projectsEnabled && (
        <div className="flex flex-wrap items-center gap-1.5">
          {((project.change_orders_pending ?? 0) > 0 || project.has_change_orders_pending) && (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700 ring-1 ring-inset ring-violet-600/20" data-testid="pm-change-orders-chip">
              <FileText className="h-3 w-3" />
              {(project.change_orders_pending ?? 0) > 0
                ? `${project.change_orders_pending} CO pending`
                : 'CO pending'}
            </span>
          )}
          {project.delay_flagged && (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-700 ring-1 ring-inset ring-red-600/20" data-testid="pm-delay-flag-chip">
              <AlertTriangle className="h-3 w-3" />
              Delay
            </span>
          )}
          {project.daily_report_overdue && (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-inset ring-amber-600/20" data-testid="pm-report-overdue-chip">
              <ClipboardList className="h-3 w-3" />
              Report overdue
            </span>
          )}
        </div>
      )}

      {inventoryEnabled && (
        <div className="flex flex-wrap items-center gap-1.5">
          {(project.gear_deployed_count ?? 0) > 0 && (
            <a
              href={project.gear_link ?? undefined}
              className="inline-flex items-center gap-0.5 rounded-full bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/20 hover:bg-indigo-100 transition-colors"
            >
              <Package className="h-3 w-3" />
              {project.gear_deployed_count} asset{project.gear_deployed_count !== 1 ? 's' : ''}
            </a>
          )}
        </div>
      )}

      <div>
        <div className="flex items-center gap-1 text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-0.5">
          <Users className="h-3 w-3" />
          Staffing
        </div>
        <span className="text-gray-700">
          {projectsEnabled && project.staffing_count != null ? project.staffing_count : techs.length} tech{(projectsEnabled && project.staffing_count != null ? project.staffing_count : techs.length) !== 1 ? 's' : ''}
        </span>
      </div>

      {showFinancials && project.daily_crew_cost && (
        <div className="flex items-center gap-1" data-testid="cost-chip">
          <DollarSign className="h-3 w-3 text-emerald-600" />
          <span className="text-emerald-700">
            Estimated crew cost: {costsRevealed ? `${formatMoney(project.daily_crew_cost, currency, OPTIONAL_FRACTION_DIGITS)}/day` : '\u2022\u2022\u2022'}
          </span>
          {onToggleCosts && (
            <button
              type="button"
              className="inline-flex h-4 w-4 items-center justify-center rounded-full text-emerald-500 hover:bg-emerald-100"
              aria-label={costsRevealed ? 'Hide estimated cost' : 'Show estimated cost'}
              data-testid="cost-toggle"
              onClick={onToggleCosts}
            >
              {costsRevealed ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
            </button>
          )}
        </div>
      )}

      <div>
        <div className="flex items-center gap-1 text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-0.5">
          <ClipboardList className="h-3 w-3" />
          Daily Report
        </div>
        {project.daily_report_today ? (
          <span className="inline-flex items-center gap-1 text-green-700">
            <Check className="h-3 w-3" />
            Submitted today
          </span>
        ) : (
          <span className="text-gray-400">&mdash;</span>
        )}
      </div>

      {safetyEvents.length > 0 && (
        <div>
          <div className="flex items-center gap-1 text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-0.5">
            <AlertTriangle className="h-3 w-3" />
            Safety Events
          </div>
          <a
            href={`/projects/${project.id}/safety`}
            className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-900 transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            <AlertTriangle className="h-3 w-3" />
            {safetyEvents.join(', ')}
          </a>
        </div>
      )}

      <div className="pt-1 border-t border-gray-100 space-y-1">
        <a
          href={`/projects/${project.id}`}
          onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-1.5 px-2 py-1.5 rounded text-gray-700 hover:bg-gray-100 transition-colors"
        >
          <FolderOpen className="h-3.5 w-3.5 text-gray-500" />
          Open project folder
        </a>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onWorkPackage?.(project.id); }}
          className="flex items-center gap-1.5 px-2 py-1.5 rounded text-gray-700 hover:bg-gray-100 transition-colors w-full text-left"
          data-testid="back-card-work-package"
        >
          <Briefcase className="h-3.5 w-3.5 text-gray-500" />
          Work package
        </button>
        {crewEnabled && canEdit && (
          <button
            type="button"
            onClick={() => onFindCrew?.(project.id)}
            className="flex items-center gap-1.5 px-2 py-1.5 rounded text-gray-700 hover:bg-gray-100 transition-colors w-full text-left"
            data-testid="find-crew"
          >
            <Users className="h-3.5 w-3.5 text-gray-500" />
            Find crew
          </button>
        )}
        {project.jsa_url && (
          <a
            href={project.jsa_url}
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-1.5 px-2 py-1.5 rounded text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <Shield className="h-3.5 w-3.5 text-gray-500" />
            Open JSA
          </a>
        )}
        {inventoryEnabled && canEdit && (
          <a
            href={`/deployments/send-gear?project=${project.id}`}
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-1.5 px-2 py-1.5 rounded text-gray-700 hover:bg-gray-100 transition-colors"
            data-testid="back-card-send-gear"
          >
            <Send className="h-3.5 w-3.5 text-gray-500" />
            Send gear
          </a>
        )}
        {project.gear_link && (
          <a
            href={project.gear_link}
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-1.5 px-2 py-1.5 rounded text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <Package className="h-3.5 w-3.5 text-gray-500" />
            Open gear list
          </a>
        )}
        {project.gear_request_link && (
          <a
            href={project.gear_request_link}
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-1.5 px-2 py-1.5 rounded text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <Truck className="h-3.5 w-3.5 text-gray-500" />
            Open gear requests
          </a>
        )}
        {project.daily_report_url && (
          <a
            href={project.daily_report_url}
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-1.5 px-2 py-1.5 rounded text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <FileText className="h-3.5 w-3.5 text-gray-500" />
            Open daily report
          </a>
        )}
      </div>
    </div>
  );
}

// ── Drag-time skill-match halo (F09) ────────────────────────────────────────

export type DragMatchStatus = 'match' | 'mismatch' | 'neutral';

export function computeDragMatchStatus(
  project: BoardProject,
  tech: BoardTech,
): DragMatchStatus {
  const req = project.staffing_requirement;
  if (!req) return 'neutral';

  const certLevel = tech.highest_cert_level ?? 0;
  const certMatch =
    (req.required_l3 > 0 && req.satisfied_l3 < req.required_l3 && certLevel >= 3) ||
    (req.required_l2 > 0 && req.satisfied_l2 < req.required_l2 && certLevel >= 2) ||
    (req.required_l1 > 0 && req.satisfied_l1 < req.required_l1 && certLevel >= 1);

  const techSkillNames = new Set(
    (tech.skills ?? []).map((s) => s.skill_name.toLowerCase()),
  );
  const skillMatch =
    req.required_skills.length > 0 &&
    req.required_skills.some((rs) => techSkillNames.has(rs.toLowerCase()));

  const hasAnyConstraint =
    req.required_l1 > 0 || req.required_l2 > 0 || req.required_l3 > 0 || req.required_skills.length > 0;
  if (!hasAnyConstraint) return 'neutral';

  return certMatch || skillMatch ? 'match' : 'mismatch';
}

// ── Drag-and-drop types ──────────────────────────────────────────────────────

interface DragItem {
  techId: number;
  personId: number;
  assignmentId?: number;
  sourceProjectId?: number;
  sourceJobId?: number;
}

interface ProjectColumnProps {
  project: BoardProject | null;
  techs: BoardTech[];
  readinessTechs?: BoardTech[];
  allTechs?: BoardTech[];
  assignments: BoardAssignment[];
  canEdit: boolean;
  crewEnabled: boolean;
  projectsEnabled: boolean;
  safetyEnabled: boolean;
  inventoryEnabled: boolean;
  payrollEnabled: boolean;
  showFinancials: boolean;
  costsRevealed?: boolean;
  onToggleCosts?: () => void;
  onDrop: (item: DragItem, targetProjectId: number | null) => void;
  selectedTechIds?: Set<number>;
  onSelectToggle?: (techId: number, shiftKey: boolean) => void;
  crewLists?: CrewListItem[];
  onAssignCrewList?: (projectId: number, techIds: number[]) => void;
  droppedTechIds?: Set<number>;
  onDropAnimationEnd?: (techId: number) => void;
  highlightExpiredCerts?: boolean;
  coLocatedCount?: number;
  coLocatedProjects?: { id: number; name: string }[];
  coLocatedFilterActive?: boolean;
  onCoLocatedClick?: () => void;
  /** When set, shown as an explanation for why drag/assign is disabled. */
  readOnlyReason?: string;
  terminology?: BoardTerminology;
  onTouchAssign?: (techId: number, techName: string, assignmentId?: number) => void;
  onWorkPackage?: (projectId: number) => void;
  onFindCrew?: (projectId: number) => void;
  /** Show supervision gap badge linking to cockpit panel */
  supervisionGap?: boolean;
  onSupervisionGapClick?: () => void;
  jobColors?: Map<number, string>;
  placementsByTech?: Map<number, number[]>;
  onBadgeClick?: (techId: number, jobId: number) => void;
  orgToday?: string;
  currency?: string;
}

export function ProjectColumn({
  project,
  techs,
  readinessTechs = techs,
  allTechs,
  assignments,
  canEdit,
  crewEnabled,
  projectsEnabled,
  safetyEnabled,
  inventoryEnabled,
  payrollEnabled,
  showFinancials,
  costsRevealed = false,
  onToggleCosts,
  onDrop,
  selectedTechIds,
  onSelectToggle,
  crewLists,
  onAssignCrewList,
  droppedTechIds,
  onDropAnimationEnd,
  highlightExpiredCerts,
  coLocatedCount,
  coLocatedProjects,
  coLocatedFilterActive,
  onCoLocatedClick,
  readOnlyReason,
  terminology = DEFAULT_TERMINOLOGY,
  onTouchAssign,
  onWorkPackage,
  onFindCrew,
  supervisionGap,
  onSupervisionGapClick,
  jobColors,
  placementsByTech,
  onBadgeClick,
  orgToday,
  currency = 'USD',
}: ProjectColumnProps) {
  const ref = useRef<HTMLDivElement>(null);
  const projectId = project?.id ?? null;
  const [crewDropdownOpen, setCrewDropdownOpen] = useState(false);
  const [flipped, setFlipped] = useState(false);
  const crewDropdownRef = useRef<HTMLDivElement>(null);
  const isTouchDevice = useIsTouchDevice();

  useEffect(() => {
    if (!crewDropdownOpen) return;
    function handleClickOutside(e: Event) {
      if (crewDropdownRef.current && !crewDropdownRef.current.contains(e.target as Node)) {
        setCrewDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [crewDropdownOpen]);

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: TECH_CARD_TYPE,
      canDrop: () => canEdit,
      drop: (item: DragItem) => {
        onDrop(item, projectId);
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [projectId, canEdit, onDrop],
  );

  // eslint-disable-next-line react-hooks/refs
  drop(ref);

  // F09: drag-time skill-match halo
  const { isDragging, dragItem } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
    dragItem: monitor.getItem() as DragItem | null,
  }));

  const dragMatchStatus: DragMatchStatus | null = useMemo(() => {
    if (!isDragging || !dragItem || !project) return null;
    const draggedTech = allTechs?.find((t) => t.id === dragItem.techId);
    if (!draggedTech) return null;
    return computeDragMatchStatus(project, draggedTech);
  }, [isDragging, dragItem, project, allTechs]);

  const assignmentMap = new Map<string, BoardAssignment>();
  const sourceProjectId = project?.id;
  const sourceJobId = project?.job_id;
  const jobColor = project ? jobColors?.get(project.job_id) : undefined;
  for (const a of assignments) {
    if (a.project_id === sourceProjectId) {
      assignmentMap.set(`${a.tech_id}`, a);
    }
  }

  const readinessReasons = project
    ? getCardReadinessReasons(project, readinessTechs, { safetyEnabled, inventoryEnabled, crewEnabled })
    : [];
  const readinessStatus = project
    ? getCardReadinessStatus(project, readinessReasons)
    : null;

  return (
    <div
      ref={ref}
      className={cn(
        project
          ? 'flex flex-col w-64 md:w-72 flex-shrink-0 rounded-lg border bg-gray-50'
          : 'flex flex-col w-64 md:w-72 flex-shrink-0 rounded-lg border-2 border-dashed border-slate-400 bg-white',
        'print:w-auto print:flex-shrink print:break-inside-avoid print:border-gray-400 print:bg-white print:mb-2',
        isOver && canDrop && 'ring-2 ring-blue-400 bg-blue-50/30',
        isOver && !canDrop && 'ring-2 ring-red-300 bg-red-50/20',
        !isOver && dragMatchStatus === 'match' && 'ring-4 ring-green-200',
        !isOver && dragMatchStatus === 'mismatch' && 'ring-4 ring-red-200',
        !isOver && dragMatchStatus === 'neutral' && 'ring-4 ring-gray-200',
      )}
      data-print-column
      data-column-kind={project ? undefined : 'bench'}
    >
      <div
        className={cn(
          'border-b rounded-t-lg print:bg-gray-100',
          !project && 'border-slate-300 bg-slate-200',
          project && flipped && 'relative z-30',
        )}
        style={project ? { perspective: '800px' } : undefined}
        data-print-column-header
      >
        {project ? (
          <div
            className="relative transition-transform duration-300 [transform-style:preserve-3d]"
            style={{ transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)' }}
          >
            <div
              className="relative px-3 py-2.5 bg-white rounded-t-lg [backface-visibility:hidden] cursor-pointer"
              role="button"
              tabIndex={!flipped ? 0 : -1}
              aria-pressed={flipped}
              aria-hidden={flipped || undefined}
              aria-label={`Flip ${project.name} column to show details`}
              onClick={() => setFlipped((previous) => !previous)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setFlipped((previous) => !previous);
                }
              }}
              data-testid="project-column-header-front"
            >
              <div className="min-h-[84px] pt-1">
                {jobColor && (
                  <div
                    className="absolute top-0 left-0 right-0 h-1 rounded-t-lg"
                    style={{ backgroundColor: jobColor }}
                    aria-hidden="true"
                    data-testid="column-color-bar"
                  />
                )}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900 truncate">
                      {project.name}
                    </h3>
                    {project.location && (
                      <p className="mt-1 inline-flex items-center gap-1 text-xs text-gray-500">
                        <MapPin className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{project.location}</span>
                      </p>
                    )}
                  </div>
                  {readinessStatus && (
                    <DispatchReadinessChip
                      status={readinessStatus}
                      reasons={readinessReasons}
                      deliveryStatus={project.dispatch_delivery_status}
                    />
                  )}
                </div>
              </div>
            </div>

            <div
              className="absolute inset-x-0 top-0 z-20 max-h-[min(70vh,36rem)] overflow-y-auto rounded-lg border-b bg-white shadow-lg [backface-visibility:hidden] cursor-pointer"
              style={{ transform: 'rotateY(180deg)' }}
              role="button"
              tabIndex={flipped ? 0 : -1}
              aria-pressed={flipped}
              aria-hidden={!flipped || undefined}
              aria-label={`Flip ${project.name} column back to summary`}
              onClick={() => setFlipped((p) => !p)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFlipped((p) => !p); } }}
              data-testid="project-column-header-back"
              data-print-hide-pocket
            >
              <div className="px-3 py-2 flex items-center justify-between border-b border-gray-100">
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-gray-900 truncate">{project.name}</h3>
                  {project.job_number && (
                    <p className="mt-0.5 text-xs font-medium text-gray-500">
                      {project.job_number}
                    </p>
                  )}
                </div>
                <RotateCcw className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
              </div>
              <ProjectBackCard
                project={project}
                techs={techs}
                terminology={terminology}
                readinessStatus={readinessStatus ?? 'ready'}
                readinessReasons={readinessReasons}
                projectsEnabled={projectsEnabled}
                inventoryEnabled={inventoryEnabled}
                crewEnabled={crewEnabled}
                showFinancials={showFinancials}
                costsRevealed={costsRevealed}
                coLocatedCount={coLocatedCount}
                coLocatedProjects={coLocatedProjects}
                coLocatedFilterActive={coLocatedFilterActive}
                supervisionGap={supervisionGap}
                onToggleCosts={onToggleCosts}
                onCoLocatedClick={onCoLocatedClick}
                onSupervisionGapClick={onSupervisionGapClick}
                onWorkPackage={onWorkPackage}
                onFindCrew={onFindCrew}
                canEdit={canEdit}
                currency={currency}
              />
              {crewEnabled && canEdit && crewLists && crewLists.length > 0 && (
                <div className="relative px-3 pb-3 print:hidden" ref={crewDropdownRef} data-print-hide-pocket>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCrewDropdownOpen((open) => !open);
                    }}
                    className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-2 py-1.5 md:py-1 rounded transition-colors"
                  >
                    <Users className="h-3 w-3" />
                    Assign Crew List
                    <ChevronDown className={cn('h-3 w-3 transition-transform', crewDropdownOpen && 'rotate-180')} />
                  </button>
                  {crewDropdownOpen && (
                    <div className="absolute left-3 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
                      {crewLists.map((crewList) => (
                        <button
                          key={crewList.id}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onAssignCrewList?.(project.id, crewList.member_ids);
                            setCrewDropdownOpen(false);
                          }}
                          className="w-full text-left px-3 py-2.5 md:py-1.5 text-xs text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                        >
                          <span className="font-medium">{crewList.name}</span>
                          <span className="text-gray-400 ml-1">({crewList.member_ids.length})</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div
            className="flex min-h-[104px] items-start justify-between gap-3 rounded-t-lg px-3 py-3"
            data-testid="unassigned-column-header"
          >
            <div>
              <h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
                <Users className="h-4 w-4 text-slate-500" aria-hidden="true" />
                Unassigned
              </h3>
              <p className="mt-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">
                Bench
              </p>
            </div>
            <span className="text-xs font-medium text-slate-500">
              {techs.length} tech{techs.length !== 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>

      {/* Tech cards */}
      <div className={cn('p-2 space-y-2 print:space-y-1 print:p-1', isTouchDevice && 'space-y-3')} data-print-tech-list>
        {techs.length === 0 && (
          project ? (
            /* F03: empty-column drop-here nudge with soft pulse */
            <div
              className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 py-6 animate-[breathe_3s_ease-in-out_infinite] print:hidden"
              data-testid="empty-drop-nudge"
            >
              <ArrowDownToLine className="h-5 w-5 text-gray-400" />
              <p className="text-xs font-medium text-gray-400">
                {readOnlyReason ? 'No technicians assigned' : 'Drop techs here'}
              </p>
              {readOnlyReason && (
                <p className="text-[10px] text-gray-400 italic px-2 text-center" data-testid="read-only-column-hint">
                  {readOnlyReason}
                </p>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-400 text-center py-4 italic">
              All techs assigned
            </p>
          )
        )}
        {techs.map((tech) => (
          <TechCard
            key={`${project?.id ?? 'unassigned'}-${tech.id}`}
            tech={tech}
            assignmentId={
              assignmentMap.get(`${tech.id}`)?.assignment_id
              ?? assignmentMap.get(`${tech.id}`)?.id
            }
            overrideReason={assignmentMap.get(`${tech.id}`)?.override_reason}
            sourceProjectId={sourceProjectId}
            sourceJobId={sourceJobId}
            canDrag={canEdit}
            canEdit={canEdit}
            crewEnabled={crewEnabled}
            inventoryEnabled={inventoryEnabled}
            payrollEnabled={payrollEnabled}
            showFinancials={showFinancials}
            currency={currency}
            isSelected={selectedTechIds?.has(tech.id)}
            onSelectToggle={onSelectToggle}
            isJustDropped={droppedTechIds?.has(tech.id)}
            onDropAnimationEnd={onDropAnimationEnd}
            highlightExpiredCerts={highlightExpiredCerts}
            onTouchAssign={onTouchAssign}
            packetStatus={assignmentMap.get(`${tech.id}`)?.packet_status ?? undefined}
            jobColors={jobColors}
            placements={placementsByTech?.get(tech.id)?.slice().sort((a, b) => a - b)}
            onBadgeClick={(jobId) => onBadgeClick?.(tech.id, jobId)}
            orgToday={orgToday}
          />
        ))}
      </div>
    </div>
  );
}
